import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import * as dates from '@simplybenefits/sb-dates';
import { getClaimCertMeta } from '../../../../actions/users.js';
import { submitDentalEDIClaim, getPendingManualTransaction } from '../../../../actions/adjudication.js';
import * as loader from '../../../../actions/loader.js';
import ClaimMetaInputModal from '../Sections/ClaimMetaInputModal.js';
import SubmitReceiptModal from '../../CreateClaim/Common/SubmitReceiptModal.js';
import AddSupportingDocsModal from '../../CreateClaim/Common/AddSupportingDocsModal.js';
import ProceduresModal from '../Sections/ProceduresModal.js';
import ExtractedTeethModal from '../Sections/ExtractedTeethModal.js';
import ReviewClaimModal from '../Sections/ReviewClaimModal.js';
import SuccessModal from '../Sections/SuccessModal.js';
import ErrorModal from '../Sections/ErrorModal.js';
import * as notifier from '../../../../actions/notifier.js';
import { createTimer } from '../../../../common/createTimeTracker.js';


class DentalClaim extends PureComponent {
    claimTimer = createTimer();

    state = {
        meta: {
            payee: null,
            cert: '',
            patient: null,
            dentist: '',
            officePostalCode: '',
            includesOrthodontic: null,
            secondaryCoverage: null,
            checkForMaxillary: null,
            checkForMandibular: null,
            isUpperPlacement: null,
            isLowerPlacement: null,
            dateOfUpperPlacement: null,
            dateOfLowerPlacement: null
        },
        reasonsForFlagging: null,
        extractedTeeth: [],
        procedures: [],
        documents: [],
        receiptPhoto: null,
        supportingPhotos: null,
        userData: null,
        currentPage: 0,
        numPages: 6,
        showSuccess: false,
        eobData: null,
        errorVerbiage: null,
        showError: false
    };

    async componentDidMount() {
        this.claimTimer.start();

        // if a cert was passed in the URL then we are loading a specific claim for a member
        if (this.props.selectedCert && this.props.selectedCert !== "null") {
            await this.setState({
                ...this.state,
                meta: {
                    ...this.state.meta,
                    cert: this.props.selectedCert
                }
            });

            await this._getCertInfo(this.props.selectedCert, this.props.selectedClaimID);
        } else if (this.props.manualTransactionUid && this.props.manualTransactionUid !== "null") {
            const manualTransactionData = await this.props.dispatch(getPendingManualTransaction(this.props.manualTransactionUid));
            const claimsBlocked = manualTransactionData?.userData?.meta?.accountClaimsBlocked || manualTransactionData?.userData?.meta?.divisionClaimsBlocked || manualTransactionData?.userData?.meta?.memberClaimsBlocked;

            if (claimsBlocked) {
                await this.props.dispatch(notifier.displayError({ verbiage: 'This member’s claims are currently blocked. This includes claim adjudication and claims submissions from all users and sources.' }));
                return;
            }
            
            await this.props.dispatch;

            if (!manualTransactionData) {
                window.location.href = '/adjudication/simplyDental';
                return;
            }

            const documents = manualTransactionData.documents || [];
            const receiptDoc = documents.find((doc) => doc.isReceipt);
            const supportingDocsList = documents.filter((doc) => !doc.isReceipt);

            /**
             * @param doc
             * @returns {Promise<{file: File, filename, contentType: *, url}>}
             */
            const fetchDocAsFile = async (doc) => {
                const response = await fetch(doc.url);
                const blob = await response.blob();
                const file = new File([ blob ], doc.filename, { type: doc.contentType });

                return {
                    file,
                    url: doc.url,
                    contentType: doc.contentType,
                    filename: doc.filename
                };
            };

            this.props.dispatch(loader.setLoader({ isLoading: true, type: 'full' }));

            const [ receiptPhoto, ...supportingPhotos ] = await Promise.all([
                receiptDoc ? fetchDocAsFile(receiptDoc) : Promise.resolve(null),
                ...supportingDocsList.map(fetchDocAsFile)
            ]);

            this.setState({
                meta: manualTransactionData.meta,
                extractedTeeth: manualTransactionData.extractedTeeth,
                procedures: manualTransactionData.procedures,
                reasonsForFlagging: manualTransactionData.reasonsForFlagging || null,
                documents,
                receiptPhoto,
                supportingPhotos: supportingPhotos.length > 0 ? supportingPhotos : null,
                userData: manualTransactionData.userData
            }, () => {
                this.props.dispatch(loader.setLoader({ isLoading: false, type: 'full' }));
            });
        }
    }

    _previous = () => {
        let index = this.state.currentPage - 1;

        if (index < 0) {
            index = 0;
            return;
        }

        this.setState({
            currentPage: index
        });
    };

    _next = () => {
        let index = this.state.currentPage + 1;

        if (index > this.state.numPages - 1) {
            index = this.state.numPages - 1;
        }

        this.setState({
            currentPage: index
        });
    };

    /**
     * When each child view is completed we call this method to save the data in state
     * @param data
     * @param type
     * @private
     */
    _saveDataInState = (data, type) => {
        this.setState({
            [type]: data
        });
    };

    _submitClaim = async () => {
        const { patient } = this.state.meta;

        const isDependent = [ 'Spouse', 'Common Law', 'Child' ].some((rel) => patient.name.includes(`(${ rel })`));

        const patientName = isDependent ? patient.name.split(' (')[0] : patient.name;
        const patientType = isDependent ? 'dependent' : 'primary';
        const dependentID = isDependent ? patient.id : null;

        // // create a Form Data form for posting the images and metadata to the server
        const form = new FormData();
        form.append('manualTransactionUid', this.props.manualTransactionUid || null);
        form.append('existingClaimID', this.props.selectedClaimID || null); // if the original claim was submitted by the member this is the claimID for that claim so we know which to remove on the backend
        form.append('receipt', this.state.receiptPhoto ? this.state.receiptPhoto.file : null);
        form.append('payee', this.state.meta.payee);
        form.append('patient', patientName);
        form.append('patientType', patientType);
        form.append('dependentID', dependentID);
        form.append('dentist', this.state.meta.dentist);
        form.append('officePostalCode', this.state.meta.officePostalCode);
        form.append('includesOrthodontic', this.state.meta.includesOrthodontic);
        form.append('secondaryCoverage', this.state.meta.secondaryCoverage);
        form.append('isUpperPlacement', this.state.meta.isUpperPlacement);
        form.append('isLowerPlacement', this.state.meta.isLowerPlacement);
        form.append('dateOfUpperPlacement', this.state.meta.dateOfUpperPlacement);
        form.append('dateOfLowerPlacement', this.state.meta.dateOfLowerPlacement);

        // need to add default items to the array so FormData actually sends an array (it's hot garbage I know)
        form.append('procedures', '0');
        form.append('procedures', '1');

        form.append('extractedTeeth', '0');
        form.append('extractedTeeth', '1');

        form.append('supportingDocs', '0');
        form.append('supportingDocs', '1');

        this.state.procedures.map((procedure) => {
            form.append("procedures", JSON.stringify({
                code: procedure.code,
                isSubstitution: procedure.isSubstitution,
                serviceDate: dates.getUTC(procedure.serviceDate),
                dentistFeeClaimed: procedure.dentistFeeClaimed,
                toothNumber: !procedure.toothNumber || procedure.toothNumber.length === 0 ? null : procedure.toothNumber,
                toothSurface: !procedure.toothSurface || procedure.toothSurface.length === 0 ? null : procedure.toothSurface,
                labProcedureCode1: procedure.labProcedureCode1.length === 0 ? null : procedure.labProcedureCode1,
                labProcedureCode2: procedure.labProcedureCode2.length === 0 ? null : procedure.labProcedureCode2,
                labProcedureFee1: procedure.labProcedureFee1.length === 0 ? null : procedure.labProcedureFee1,
                labProcedureFee2: procedure.labProcedureFee2.length === 0 ? null : procedure.labProcedureFee2,
                typeCodes: procedure.typeCodes.length === 0 ? null : procedure.typeCodes,
                overrideAdjudication: Boolean(procedure.overrideAdjudication),
                dentistFeeAdjudicatedAmount: procedure.dentistFeeAdjudicatedAmount || null,
                dentistFeeDeductible: procedure.dentistFeeDeductible || null,
                labProcedureFee1AdjudicatedAmount: procedure.labProcedureFee1AdjudicatedAmount || null,
                labProcedureFee1DeductibleAmount: procedure.labProcedureFee1DeductibleAmount || null,
                labProcedureFee2AdjudicatedAmount: procedure.labProcedureFee2AdjudicatedAmount || null,
                labProcedureFee2DeductibleAmount: procedure.labProcedureFee2DeductibleAmount || null,
                overrideNotes: procedure.overrideNotes || null
            }));

            return true;
        });

        this.state.extractedTeeth && this.state.extractedTeeth.map((extractedTooth) => {
            form.append("extractedTeeth", JSON.stringify({
                serviceDate: dates.getUTC(extractedTooth.extractionDate),
                toothNumber: extractedTooth.toothNumber
            }));

            return true;
        });

        // attach each supporting document to the supporting docs array
        this.state.supportingPhotos && this.state.supportingPhotos.map((item) => {
            form.append("supportingDocs", item.file);
            return true;
        });

        const claimCreationTimeSeconds = this.claimTimer.stop();

        form.append("claimCreationTimeSeconds", claimCreationTimeSeconds);

        const data = await this.props.dispatch(submitDentalEDIClaim(this.state.userData.meta.userID, form));

        if (data?.type?.toUpperCase() === 'SUCCESS') {
            this.setState({
                showSuccess: true,
                eobData: data
            });
        } else if (!data || !data.type || data?.type?.toUpperCase() === 'FAILED' || data?.type?.toUpperCase() === 'FAIL') {
            this.setState({
                showError: true,
                errorVerbiage: data.errors || data.verbiage,
                eobData: data
            });
        }
    };

    _getCertInfo = async (cert, claimID = null) => {
        if (!cert || cert.length <= 0) {
            return;
        }

        // if a claimID is passed we will use it to get the correct patient
        const userData = await this.props.dispatch(getClaimCertMeta(cert, claimID));

        if (userData) {
            this.setState({
                userData,
                meta: {
                    ...this.state.meta,
                    patient: userData.meta.patient
                }
            });
        }
    };

    render() {
        // if we are loading a claim for a member and not just the blank modal then ensure we have the user data before loading the modal
        if (this.props.selectedCert && this.props.selectedCert !== "null" && !this.state.userData) {
            return null;
        }

        const isReferredPendingManualEdiClaim = this.props.manualTransactionUid && this.props.manualTransactionUid !== "null";

        if (this.state.showSuccess) {
            return (
                <SuccessModal
                    type="Claim"
                    eob={ this.state.eobData.EOB }
                    id={ this.state.eobData.id }
                    onClose={ () => {
                        if (this.props.manualTransactionUid) {
                            window.location.href = '/adjudication/simplyDental';
                        } else {
                            this.props.close();
                        }
                    }}
                />
            );
        }

        if (this.state.showError) {
            return (
                <ErrorModal
                    type="Claim"
                    errors={ this.state.errorVerbiage}
                    onClose={ () => {
                        if (this.props.manualTransactionUid) {
                            window.location.href = '/adjudication/simplyDental';
                        } else {
                            this.props.close();
                        }
                    }}
                />
            );
        }

        return (
            <>
                {
                    this.state.currentPage === 0 &&
                        <ClaimMetaInputModal
                            title={`Submit First Payor Claim`}
                            onClose={ this.props.close }
                            next={ this._next }
                            meta={ this.state.meta }
                            saveData={ this._saveDataInState }
                            type={ this.props.type }
                            userData={ this.state.userData }
                            getCertInfo={ this._getCertInfo }
                            isReferredPendingManualEdiClaim={isReferredPendingManualEdiClaim}
                            documents={ this.state.documents }
                            reasonsForFlagging={ this.state.reasonsForFlagging }
                        />
                }

                {
                    this.state.currentPage === 1 &&
                        <ProceduresModal
                            title={`Submit First Payor Claim`}
                            onBack={ this._previous }
                            onClose={ this.props.close }
                            next={ this._next }
                            procedures={ this.state.procedures }
                            saveData={ this._saveDataInState }
                            type={ this.props.type }
                            userData={ this.state.userData }
                        />
                }

                {
                    this.state.currentPage === 2 &&
                        <ExtractedTeethModal
                            title={`Submit First Payor Claim`}
                            onBack={ this._previous }
                            onClose={ this.props.close }
                            next={ this._next }
                            extractedTeeth={ this.state.extractedTeeth }
                            saveData={ this._saveDataInState }
                            type={ this.props.type }
                            userData={ this.state.userData }
                        />
                }

                {
                    this.state.currentPage === 3 &&
                        <SubmitReceiptModal
                            title={`Submit First Payor Claim`}
                            onBack={ this._previous }
                            onClose={ this.props.close }
                            next={ this._next }
                            receiptPhoto={ this.state.receiptPhoto }
                            saveData={ this._saveDataInState }
                            type={ this.props.type }
                            userData={ this.state.userData }
                            receiptRequired={ false }
                        />
                }

                {
                    this.state.currentPage === 4 &&
                        <AddSupportingDocsModal
                            title={`Submit First Payor Claim`}
                            onBack={ this._previous }
                            onClose={ this.props.close }
                            next={ this._next }
                            supportingPhotos={this.state.supportingPhotos}
                            saveData={ this._saveDataInState }
                            type={ this.props.type }
                            userData={ this.state.userData }
                        />
                }

                {
                    this.state.currentPage === 5 &&
                        <ReviewClaimModal
                            title={`Submit First Payor Claim`}
                            onBack={ this._previous }
                            onClose={ this.props.close }
                            meta={ this.state.meta }
                            procedures={ this.state.procedures }
                            extractedTeeth={ this.state.extractedTeeth }
                            receiptPhoto={ this.state.receiptPhoto }
                            supportingPhotos={ this.state.supportingPhotos }
                            submitClaim={ this._submitClaim }
                            loading={ this.props.loading }
                            userData={ this.state.userData }
                            type="Claim"
                        />
                }
            </>
        );
    }
}

DentalClaim.propTypes = {
    master: PropTypes.object
};

/**
 *
 * @param {*} state
 */
const mapStateToProps = (state) => ({
    loader: state.loader,
    master: state.master,
    submitClaimLoading: state.adjudication.loading
});

export default connect(mapStateToProps)(DentalClaim);